import { api } from "@/lib/api-client"

export type PluginKind = "openapi" | "mcp" | "native"
export type AuthKind = "none" | "api_key" | "bearer" | "oauth" | "entra"
export type PluginSurface = "explorer" | "deep" | "voice" | "flows" | "dash"
export interface PluginConnection {
  id: string
  plugin_id: string
  owner_id: string | null
  mode: "personal" | "company"
  auth_kind: AuthKind
  label: string
  status: "pending" | "connected" | "reauth_required" | "disconnected"
  revision: number
}
export interface PluginFlowGrant {
  id: string
  flow_id: string
  flow_title?: string
  connection_id: string
  enabled: boolean
}
export interface PluginEntry {
  id: string
  name: string
  description: string
  publisher: string
  plugin_kind: PluginKind
  enabled: boolean
  surfaces: PluginSurface[]
  self_install: boolean
  installed: boolean
  managed: boolean
  version_id: string | null
  latest_version_id: string | null
  update_available: boolean
  connection: PluginConnection | null
  auth_kind: AuthKind
  credential_mode: "company" | "personal"
  permissions: string[]
  latest_permissions: string[]
  latest_operation_count: number
  latest_endpoint: string | null
  operation_count: number
  revision: number
}
export interface PluginOperation {
  id: string
  description: string
  parameters: Record<string, unknown>
}
export interface Audience {
  everyone: boolean
  users: string[]
  groups: string[]
  labels?: Record<string, string>
}
export interface AdminPlugin {
  id: string
  name: string
  description: string
  publisher: string
  plugin_kind: PluginKind
  enabled: boolean
  self_install: boolean
  audience: Audience
  surfaces: PluginSurface[]
  published_version: string | null
  template_id?: string | null
  template_version?: number | null
  revision: number
}
export interface PluginDistribution {
  id: string
  plugin_id: string
  version_id: string
  audience: Audience
  credential_mode: "personal" | "company"
  connection_id: string | null
  enabled: boolean
  revision: number
}
export interface PluginAuth {
  kind: AuthKind
  header_name?: string
  issuer?: string
  authorization_endpoint?: string
  token_endpoint?: string
  revocation_endpoint?: string
  client_id?: string
  client_secret_connection_id?: string
  scopes?: string[]
  resource?: string
}
export interface PluginVersion {
  id: string
  plugin_id: string
  version: number
  plugin_kind: PluginKind
  operations: PluginOperation[]
  auth: PluginAuth
  limits: PluginLimits
  base_url: string | null
  headers?: Record<string, string>
  source_blob?: string | null
  network?: { private_cidrs: string[] }
  transport?: "streamable_http" | "sse"
}
export interface PluginLimits {
  timeout_seconds: number
  response_bytes: number
  pages: number
  calls: number
  concurrency: number
  per_minute: number
  model_bytes: number
}
export interface PluginTemplate {
  id: string
  version: number
  name: string
  description: string
  plugin_kind: PluginKind
  defaults: {
    tool_name?: string
    approved_actions?: string[]
    base_url?: string
    auth: PluginAuth
    tool_manifest?: unknown[]
  }
}
export type FlowPluginBindings = Partial<
  Record<string, { operation_ids: string[]; connection_id?: string | null }>
>
export interface BackgroundAccount {
  connection_id: string | null
  status: "pending" | "connected" | "reauth_required" | "disconnected"
  generation: number | null
  granted?: boolean
}
export interface PluginConsentPreview {
  approval_hash: string
  assignments?: {
    name: string
    account_mode: "company" | "personal" | "none"
    operations: string[]
  }[]
  identity?: BackgroundAccount | null
  flow_hash: string
  requires_consent: boolean
  connections: { connection_id: string; plugin_id: string; name: string; granted: boolean }[]
}
export interface McpMigrationPreview {
  agent: string
  label: string
  source_hash: string
  base_url?: string | null
  allowed_tools?: string[] | null
  auth_kind?: AuthKind
  audience?: Audience
  blockers: string[]
  resume: boolean
  phase?: string
  transport: "streamable_http" | "sse"
  network: { private_cidrs: string[] }
}

// api-client's success interceptor returns response.data. Axios 1.20's
// conditional default-response marker cannot resolve for an unconstrained T;
// these narrow assertions preserve that existing payload boundary.
async function get<T>(path: string): Promise<T> {
  return api.get<T, T>(path) as Promise<T>
}
async function post<T>(path: string, body: unknown): Promise<T> {
  return api.post<T, T>(path, body) as Promise<T>
}
async function put<T>(path: string, body: unknown): Promise<T> {
  return api.put<T, T>(path, body) as Promise<T>
}
const id = encodeURIComponent
export const pluginsApi = {
  backgroundAccount: () => get<BackgroundAccount>("/plugins/background-account"),
  startBackgroundAccount: (flowId?: string) =>
    post<{ authorization_url: string }>("/plugins/background-account/start", { flow_id: flowId }),
  disconnectBackgroundAccount: () => api.delete("/plugins/background-account"),
  configureAccess: (
    plugin: AdminPlugin,
    distribution: PluginDistribution,
    changes: { catalog: Partial<AdminPlugin>; distribution: Partial<PluginDistribution> }
  ) =>
    put(`/admin/plugins/${id(plugin.id)}/access`, {
      catalog_revision: plugin.revision,
      distribution_revision: distribution.revision,
      catalog: changes.catalog,
      distribution: changes.distribution,
    }),
  grants: (connectionId: string) =>
    get<{ consents: PluginFlowGrant[] }>(`/plugins/connections/${id(connectionId)}/consents`),
  revokeGrant: (connectionId: string, grantId: string) =>
    api.delete(`/plugins/connections/${id(connectionId)}/consents/${id(grantId)}`),
  disconnectCompany: (pluginId: string) => api.delete(`/admin/plugins/${id(pluginId)}/connections`),
  templates: () => get<{ templates: PluginTemplate[] }>("/admin/plugins/templates"),
  instantiate: (templateId: string) =>
    post<{ plugin: AdminPlugin }>(`/admin/plugins/templates/${id(templateId)}/instantiate`, {}),
  source: (pluginId: string, versionId: string) =>
    get<{ source: string }>(`/admin/plugins/${id(pluginId)}/versions/${id(versionId)}/source`),
  delete: (plugin: AdminPlugin) =>
    api.delete(`/admin/plugins/${id(plugin.id)}`, { data: { revision: plugin.revision } }),
  migrationPreview: (
    agent: string,
    label: string,
    options: Pick<McpMigrationPreview, "transport" | "network">
  ) => post<McpMigrationPreview>("/admin/plugins/migrations/preview", { agent, label, ...options }),
  migrate: (preview: McpMigrationPreview, approvedNames: string[], confirmedReadOnly: boolean) =>
    post("/admin/plugins/migrations/commit", {
      agent: preview.agent,
      label: preview.label,
      expected_hash: preview.source_hash,
      approved_names: approvedNames,
      confirmed_read_only: confirmedReadOnly,
      transport: preview.transport,
      network: preview.network,
    }),
  consentPreview: (flowId: string, settings: Record<string, unknown>) =>
    post<PluginConsentPreview>(`/plugins/flows/${id(flowId)}/consent/preview`, settings),
  grantConsent: (
    flowId: string,
    settings: Record<string, unknown>,
    reviewed: Pick<PluginConsentPreview, "flow_hash" | "approval_hash">
  ) =>
    post(`/plugins/flows/${id(flowId)}/consent`, {
      ...settings,
      expected_hash: reviewed.flow_hash,
      expected_approval_hash: reviewed.approval_hash,
    }),
  catalog: () => get<{ enabled: boolean; items: PluginEntry[] }>("/plugins/catalog"),
  operations: (pluginId: string, versionId?: string) =>
    get<{ operations: PluginOperation[] }>(
      `/plugins/catalog/${id(pluginId)}/operations${versionId ? `?version=${id(versionId)}` : ""}`
    ),
  install: (pluginId: string, versionId: string, upgrade = false) =>
    post("/plugins/installations", { plugin_id: pluginId, version_id: versionId, upgrade }),
  uninstall: (pluginId: string) => api.delete(`/plugins/installations/${id(pluginId)}`),
  connect: (pluginId: string, kind: "api_key" | "bearer", credential: string) =>
    post("/plugins/connections", { plugin_id: pluginId, kind, credential }),
  disconnect: (connectionId: string) => api.delete(`/plugins/connections/${id(connectionId)}`),
  startOAuth: (pluginId: string) =>
    post<{ authorization_url: string }>("/plugins/oauth/start", { plugin_id: pluginId }),
  finishOAuth: (state: string, code: string) =>
    post<{ success: boolean; return_path?: string }>("/plugins/oauth/callback", { state, code }),
  adminList: () =>
    get<{ settings: { enabled: boolean; revision: number }; items: AdminPlugin[] }>(
      "/admin/plugins"
    ),
  setEnabled: (enabled: boolean, revision: number) =>
    put("/admin/plugins/settings", { enabled, revision }),
  create: (values: { name: string; description: string; plugin_kind: PluginKind }) =>
    post<{ plugin: AdminPlugin }>("/admin/plugins", values),
  update: (plugin: AdminPlugin, values: Partial<AdminPlugin>) =>
    put<{ plugin: AdminPlugin }>(`/admin/plugins/${id(plugin.id)}`, {
      revision: plugin.revision,
      values,
    }),
  distribution: (pluginId: string) =>
    get<{ distribution: PluginDistribution }>(`/admin/plugins/${id(pluginId)}/distribution`),
  distribute: (pluginId: string, revision: number, values: Partial<PluginDistribution>) =>
    put(`/admin/plugins/${id(pluginId)}/distribution`, { revision, values }),
  versions: (pluginId: string) =>
    get<{ versions: PluginVersion[] }>(`/admin/plugins/${id(pluginId)}/versions`),
  preview: (plugin: AdminPlugin, values: Record<string, unknown>) =>
    post<{ operations: PluginOperation[] }>(
      plugin.plugin_kind === "mcp"
        ? `/admin/plugins/${id(plugin.id)}/mcp/preview`
        : "/admin/plugins/openapi/preview",
      values
    ),
  publish: (plugin: AdminPlugin, values: Record<string, unknown>) =>
    post<{ plugin: AdminPlugin }>(
      `/admin/plugins/${id(plugin.id)}/${plugin.plugin_kind === "openapi" ? "publish" : plugin.plugin_kind + "/publish"}`,
      { ...values, revision: plugin.revision }
    ),
  connectCompany: (pluginId: string, kind: "api_key" | "bearer", credential: string) =>
    post<{ connection: PluginConnection }>(`/admin/plugins/${id(pluginId)}/connections`, {
      kind,
      credential,
    }),
  companyConnection: (pluginId: string) =>
    get<{ connection: PluginConnection | null }>(`/admin/plugins/${id(pluginId)}/connections`),
  companyOAuth: (pluginId: string) =>
    post<{ authorization_url: string }>(`/admin/plugins/${id(pluginId)}/oauth/start`, {}),
  oauthClient: (pluginId: string, clientSecret: string) =>
    post<{ client_secret_connection_id: string }>(`/admin/plugins/${id(pluginId)}/oauth/client`, {
      client_secret: clientSecret,
    }),
  test: (pluginId: string, operationId: string, args: Record<string, unknown>) =>
    post<{ success: boolean; error?: string }>(`/admin/plugins/${id(pluginId)}/test`, {
      operation_id: operationId,
      arguments: args,
    }),
}
